import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BiHide, BiShow, BiTrash } from 'react-icons/bi';

import {
  PADDOCKS_MENU_DELETE_LABEL,
  PADDOCKS_MENU_EXCLUDE_LABEL,
  PADDOCKS_MENU_INCLUDE_LABEL,
} from 'constants/uiConstants';
import { pxToRem } from 'utils/cssUnits';

/** Breathing room kept between the menu and the edge of the viewport. */
const VIEWPORT_MARGIN_PX = 8;

interface PaddocksContextMenuProps {
  x: number;
  y: number;
  /** Ticked rows the menu acts on. */
  count: number;
  canExclude: boolean;
  canInclude: boolean;
  onExclude: () => void;
  onInclude: () => void;
  onDelete: () => void;
  onClose: () => void;
}

// Item markup matches the Tasks grid's row menu (TaskRowContextMenu): grey
// text at the grid's own size (text-base, 14px), a
// secondary-dark Bi icon.
const MenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    type="button"
    role="menuitem"
    className="flex w-full items-center gap-1 px-3 py-2 text-left text-base text-gray-dark hover:bg-rowHover"
    onClick={onClick}
  >
    {icon}
    <span>{label}</span>
  </button>
);

/**
 * The Paddocks grid's right-click menu (OP-2481): Exclude, Include and
 * Delete for the ticked rows, the same as the action bar. Styled as the
 * Tasks grid's row menu and positioned at the pointer, kept inside the
 * viewport (`.row-actions-menu`). Escape, a click elsewhere or scrolling
 * closes it.
 */
const PaddocksContextMenu: React.FC<PaddocksContextMenuProps> = ({
  x,
  y,
  count,
  canExclude,
  canInclude,
  onExclude,
  onInclude,
  onDelete,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const { width, height } = element.getBoundingClientRect();
    setPosition({
      left: Math.max(
        VIEWPORT_MARGIN_PX,
        Math.min(x, window.innerWidth - width - VIEWPORT_MARGIN_PX)
      ),
      top: Math.max(
        VIEWPORT_MARGIN_PX,
        Math.min(y, window.innerHeight - height - VIEWPORT_MARGIN_PX)
      ),
    });
    element.querySelector<HTMLButtonElement>('button')?.focus();
  }, [x, y]);

  useEffect(() => {
    const handlePointer = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  const run = (action: () => void) => () => {
    onClose();
    action();
  };

  return (
    <div
      ref={ref}
      role="menu"
      className="row-actions-menu fixed z-[60] w-max rounded-md border border-gray-bright bg-white shadow-lg"
      // Runtime coordinates, consumed by .row-actions-menu in App.css
      style={
        {
          '--row-menu-left': `${pxToRem(position.left)}rem`,
          '--row-menu-top': `${pxToRem(position.top)}rem`,
        } as React.CSSProperties
      }
    >
      {canExclude && (
        <MenuItem
          icon={<BiHide aria-hidden className="text-base text-secondary-dark" />}
          label={PADDOCKS_MENU_EXCLUDE_LABEL(count)}
          onClick={run(onExclude)}
        />
      )}
      {canInclude && (
        <MenuItem
          icon={<BiShow aria-hidden className="text-base text-secondary-dark" />}
          label={PADDOCKS_MENU_INCLUDE_LABEL(count)}
          onClick={run(onInclude)}
        />
      )}
      <MenuItem
        icon={<BiTrash aria-hidden className="text-base text-secondary-dark" />}
        label={PADDOCKS_MENU_DELETE_LABEL(count)}
        onClick={run(onDelete)}
      />
    </div>
  );
};

export default PaddocksContextMenu;
